import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../i18n';
import { ArrowLeft, ChevronRight } from 'lucide-react';

const sections = [
  { id: 'editeur', label: '1. Éditeur du site' },
  { id: 'directeur', label: '2. Directeur de publication' },
  { id: 'hebergeur', label: '3. Hébergeur' },
  { id: 'propriete', label: '4. Propriété intellectuelle' },
  { id: 'credits', label: '5. Crédits' },
];

const MentionsLegales = () => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('editeur');

  useEffect(() => {
    const handleScroll = () => {
      const offsets = sections.map(({ id }) => {
        const el = document.getElementById(id);
        return { id, top: el ? el.getBoundingClientRect().top : Infinity };
      });
      const current = offsets.filter((o) => o.top <= 120).pop();
      if (current) setActiveSection(current.id);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-nexus-600 transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à l'accueil
          </Link>
          <h1 className="text-3xl font-bold text-slate-900">
            Mentions Légales
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Dernière mise à jour : 17 avril 2026
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex gap-10">
          {/* Sidebar navigation (desktop only) */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <nav className="sticky top-24 space-y-1">
              {sections.map(({ id, label }) => (
                <a
                  key={id}
                  href={`#${id}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                    activeSection === id
                      ? 'bg-nexus-50 text-nexus-700 font-medium'
                      : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  {activeSection === id && <ChevronRight className="w-3 h-3 flex-shrink-0" />}
                  <span>{label}</span>
                </a>
              ))}
            </nav>
          </aside>

          {/* Main content */}
          <div className="flex-1 min-w-0">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 sm:p-8 lg:p-10">
              <div className="prose prose-slate max-w-none prose-headings:scroll-mt-24">
                <p className="text-slate-600 leading-relaxed">
                  Conformément aux dispositions des articles 6-III et 19 de la loi n° 2004-575 du 21 juin
                  2004 pour la confiance dans l'économie numérique (LCEN), il est porté à la connaissance
                  des utilisateurs du site <strong>Nexus-360</strong> les informations suivantes :
                </p>

                {/* 1. Éditeur du Site */}
                <h2 id="editeur" className="text-xl font-bold text-slate-900 mt-10 mb-4">
                  1. Éditeur du Site
                </h2>
                <div className="bg-slate-50 rounded-lg p-5 text-slate-600 space-y-2">
                  <p><strong>Raison sociale :</strong> ACL Gestion</p>
                  <p><strong>Forme juridique :</strong> Société par actions simplifiée (SAS)</p>
                  <p><strong>Adresse du siège social :</strong> Paris, France</p>
                  <p><strong>SIRET :</strong> XXX XXX XXX XXXXX <em>(en cours d'immatriculation)</em></p>
                  <p><strong>E-mail :</strong>{' '}
                    <a href="mailto:contact@nexus-360.fr" className="text-nexus-600 hover:underline">
                      contact@nexus-360.fr
                    </a>
                  </p>
                  <p><strong>Site web :</strong>{' '}
                    <a href="https://nexus-360.fr" className="text-nexus-600 hover:underline">
                      https://nexus-360.fr
                    </a>
                  </p>
                </div>

                {/* 2. Directeur de Publication */}
                <h2 id="directeur" className="text-xl font-bold text-slate-900 mt-10 mb-4">
                  2. Directeur de la Publication
                </h2>
                <div className="bg-slate-50 rounded-lg p-5 text-slate-600 space-y-2">
                  <p><strong>Nom :</strong> Avishka Liyanaarachchi</p>
                  <p><strong>Qualité :</strong> Dirigeant d'ACL Gestion</p>
                  <p><strong>Contact :</strong>{' '}
                    <a href="mailto:contact@nexus-360.fr" className="text-nexus-600 hover:underline">
                      contact@nexus-360.fr
                    </a>
                  </p>
                </div>
                <p className="text-slate-600 leading-relaxed mt-3">
                  Le directeur de la publication est responsable du contenu éditorial publié sur le site
                  conformément à l'article 93-2 de la loi n° 82-652 du 29 juillet 1982 sur la communication
                  audiovisuelle.
                </p>

                {/* 3. Hébergeur */}
                <h2 id="hebergeur" className="text-xl font-bold text-slate-900 mt-10 mb-4">
                  3. Hébergeur
                </h2>
                <div className="bg-slate-50 rounded-lg p-5 text-slate-600 space-y-2">
                  <p><strong>Raison sociale :</strong> OVH SAS</p>
                  <p><strong>Adresse :</strong> 2, rue Kellermann, 59100 Roubaix, France</p>
                  <p><strong>Téléphone :</strong> +33 (0)8 99 70 17 61</p>
                  <p><strong>Site web :</strong>{' '}
                    <a
                      href="https://www.ovhcloud.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-nexus-600 hover:underline"
                    >
                      www.ovhcloud.com
                    </a>
                  </p>
                </div>
                <p className="text-slate-600 leading-relaxed mt-3">
                  L'ensemble des données de la Plateforme sont hébergées sur des serveurs situés en France
                  (datacenters de Roubaix et Gravelines), conformément aux exigences de souveraineté des
                  données et au RGPD.
                </p>

                {/* 4. Propriété Intellectuelle */}
                <h2 id="propriete" className="text-xl font-bold text-slate-900 mt-10 mb-4">
                  4. Propriété Intellectuelle
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  L'ensemble du contenu du site Nexus-360, incluant de manière non limitative les textes,
                  graphismes, images, photographies, logos, icônes, sons, logiciels, code source, bases de
                  données et la structure générale du site, est la propriété exclusive d'ACL Gestion ou de
                  ses partenaires et est protégé par les lois françaises et internationales relatives à la
                  propriété intellectuelle.
                </p>
                <p className="text-slate-600 leading-relaxed mt-3">
                  Toute reproduction, représentation, modification, publication, adaptation, transmission,
                  dénaturation, totale ou partielle du site ou de son contenu, par quelque procédé que ce
                  soit et sur quelque support que ce soit, est interdite sans l'autorisation écrite préalable
                  d'ACL Gestion. Toute exploitation non autorisée du site ou de son contenu sera considérée
                  comme constitutive d'une contrefaçon et poursuivie conformément aux dispositions des
                  articles L.335-2 et suivants du Code de la propriété intellectuelle.
                </p>
                <p className="text-slate-600 leading-relaxed mt-3">
                  Le nom « Nexus-360 », le logo et l'ensemble des marques, signes distinctifs et noms de
                  domaine associés sont la propriété d'ACL Gestion. Toute utilisation non autorisée de ces
                  éléments engage la responsabilité de son auteur.
                </p>

                {/* 5. Crédits */}
                <h2 id="credits" className="text-xl font-bold text-slate-900 mt-10 mb-4">
                  5. Crédits
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  La Plateforme Nexus-360 a été conçue et développée par <strong>ACL Gestion</strong>.
                </p>
                <ul className="space-y-2 text-slate-600 mt-3">
                  <li><strong>Conception et développement :</strong> ACL Gestion</li>
                  <li><strong>Icônes :</strong> Lucide React (<a href="https://lucide.dev" target="_blank" rel="noopener noreferrer" className="text-nexus-600 hover:underline">lucide.dev</a>) — Licence ISC</li>
                  <li><strong>Typographie :</strong> Inter (Google Fonts) — Licence SIL Open Font</li>
                  <li><strong>Framework CSS :</strong> Tailwind CSS — Licence MIT</li>
                </ul>

                {/* Links to other legal pages */}
                <div className="mt-10 p-6 bg-slate-50 rounded-xl border border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">Documents juridiques</h3>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link
                      to="/cgu"
                      className="inline-flex items-center gap-2 text-nexus-600 hover:text-nexus-700 font-medium text-sm"
                    >
                      <ChevronRight className="w-4 h-4" />
                      Conditions Générales d'Utilisation
                    </Link>
                    <Link
                      to="/confidentialite"
                      className="inline-flex items-center gap-2 text-nexus-600 hover:text-nexus-700 font-medium text-sm"
                    >
                      <ChevronRight className="w-4 h-4" />
                      Politique de Confidentialité
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MentionsLegales;
